import { RequiredTreeNodeData, TreeChildrenResponse, TreeDetails, TreeNodeData } from '../types';
import { NodeData } from '../../../ui/gzTree/type';
import {
  LicenseType,
  ProtectedEntity,
  ProtectedEntityType,
} from '@nirvana/shared/dtos/types/protected-entities/common';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../utils/translator.helper';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { NetworkIconHelper } from '../../networkAppContainer/helpers/icons/network.icon.helper';
import { IconLocation, TreeProtectedEntity } from '../../networkAppContainer/helpers/types';
import { IconProperties } from '../../../ui/gzIcon/types';

export function transformTreeStructureToParentAndChildNodes(
  response: TreeChildrenResponse,
  targetNode: TreeNodeData
): { currentNode: NodeData; childNodes: NodeData[]; hasMoreChildNodes: boolean } {
  const currentNode: NodeData = {
    ...targetNode,
    childNodeIds: response.data.map(child => child._id),
    isOpen: true, // the parent got opened
  };

  targetNode.originalData.realNode = response.realNode;
  const childNodes: NodeData[] = response.data.map(child => {
    return transformProtectedEntityToNodeData(child);
  });

  return {
    currentNode,
    childNodes,
    hasMoreChildNodes: response.hasMoreChildNodes,
  };
}

export function treePrefixRenderer(protectedEntity: RequiredTreeNodeData, usersRootNode?: boolean): TemplateResult {
  const iconProperties: IconProperties = NetworkIconHelper.getIconForProtectedEntity(
    protectedEntity as ProtectedEntity,
    IconLocation.TREE
  );

  const defaultRenderer = html`<gz-icon name="${iconProperties.icon}" color="secondary" size="24"></gz-icon>`;

  // the root company has also canBeManagedByAbove === false, but no other entity than it has no parentId
  if (
    protectedEntity.type !== Constants.Companies.TYPE_COMPANY ||
    protectedEntity.specifics?.canBeManagedByAbove ||
    !protectedEntity.parentId ||
    usersRootNode
  ) {
    return defaultRenderer;
  } else {
    const tooltipText = protectedEntity.specifics.rights?.manageCompanies
      ? translateFn('Self-managed partner company. No rights to view or make changes.')
      : translateFn('Self-managed customer company. No rights to view or make changes.');
    return html`<gz-tooltip tooltipText=${tooltipText}> ${defaultRenderer} </gz-tooltip>`;
  }
}

export function transformProtectedEntityToNodeData(protectedEntity: TreeProtectedEntity): NodeData {
  const isDisabled =
    protectedEntity.type === Constants.Companies.TYPE_COMPANY &&
    protectedEntity.specifics?.canBeManagedByAbove === false;
  const requiredTreeData = transformProtectedEntityToRequiredTreeNodeData(protectedEntity);

  return {
    nodeId: protectedEntity._id,
    nodeName: Translator.translateProtectedEntityNameByType(
      protectedEntity.name,
      protectedEntity.type,
      Constants.ProductTypes.GRAVITY_ZONE_CLOUD
    ),
    originalData: requiredTreeData,
    isDisabled: isDisabled ? true : undefined,
    hasMoreChildNodes: !!protectedEntity.hasMoreChildNodes,
    namePrefixRenderer: () => treePrefixRenderer(requiredTreeData),
  };
}

export function transformProtectedEntityToRequiredTreeNodeData(
  protectedEntity: TreeProtectedEntity
): RequiredTreeNodeData {
  const hasMspLicense: boolean =
    protectedEntity.type === ProtectedEntityType.TYPE_COMPANY &&
    (protectedEntity.specifics.license?.type === LicenseType.MONTHLY ||
      protectedEntity.specifics.licenses?.[0].type === LicenseType.MONTHLY);

  return {
    _id: protectedEntity._id,
    type: protectedEntity.type,
    parentId: protectedEntity.parentId,
    company: protectedEntity.company,
    name: protectedEntity.name,
    path: protectedEntity.path,
    hasMspLicense: hasMspLicense,
    specifics: {
      hasMspTrial: protectedEntity.specifics?.hasMspTrial ?? false,
      inheritedLicense: protectedEntity.specifics?.inheritedLicense ?? false,
      canBeManagedByAbove: protectedEntity.specifics?.canBeManagedByAbove ?? false,
      rights: {
        manageCompanies: protectedEntity.specifics?.rights?.manageCompanies ?? false,
      },
      maStatus: protectedEntity.specifics?.maStatus,
      productType: protectedEntity.specifics?.productType,
      stateCode: protectedEntity.specifics?.stateCode,
    },
    realNode: protectedEntity.realNode ? protectedEntity.realNode : undefined,
  };
}

export function getViewTypeFromType(type: number): number {
  const companiesViewTypes = [
    Constants.Companies.TYPE_COMPANY,
    Constants.Companies.TYPE_COMPANIES_COLLECTION,
    Constants.Companies.TYPE_COMPANY_NETWORK,
    Constants.Companies.TYPE_COMPANY_FOLDER,
  ];

  return companiesViewTypes.includes(type)
    ? Constants.Companies.viewType.COMPANIES_VIEW
    : Constants.ProtectedEntities.EPS.viewType.EPS_TREE_VIEW;
}

export async function getTreeDetailsForCompanyId(companyId: string): Promise<TreeDetails> {
  const response = await ConsoleRequest.createRequest<any>({
    dataProviderType: DataProviderType.CORE,
    endpoint: 'Company',
    method: 'getCompanyById',
    payload: {
      companyId: companyId,
    },
  });

  if (!response.success || !response.data) {
    throw new Error('Unable to get company name for filter');
  }

  return {
    targetId: response.data._id,
    type: response.data.type,
    path: response.data.path,
    parentId: response.data.parentId,
  };
}
